<template>
  <div class="flex flex-col gap-y-4">
    <div>
      <h3>
        <Translation
          name="calculation_wizard.preview.sidebar_wages"
          default="Wages"
        />
      </h3>
      <div class="rounded-xl bg-[#3B3B44] p-4">
        <div class="flex flex-col gap-y-2 mb-6">
          <span class="text-white font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_you_entered"
              default="You entered"
            />
          </span>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_wages_ord_hours"
                default="Ordinary hours worked in the year"
              />
            </span>
            <div class="value-tag">{{ hoursWorked }} hrs</div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_wage_earned"
                default="Wage earned during ordinary hours"
              />
            </span>
            <div class="value-tag">
              {{ wageEarned ? wageEarned.toFixed(2) : "-" }}
              {{ currency }}
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-y-2">
          <span class="text-white font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_chart_shows"
              default="The chart shows"
            />
          </span>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_wages_monthly_full_time_wage"
                default="Comparable monthly full-time wage"
              />
            </span>
            <div class="value-tag">
              {{
                comparableMonthlyWage ? comparableMonthlyWage.toFixed(2) : "-"
              }}
              {{ currency }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <h3>
        <Translation
          name="calculation_wizard.preview.sidebar_calculation"
          default="Calculation"
        />
      </h3>
      <div class="flex flex-col gap-y-2">
        <!-- Step 1: Full-time working hours calculation -->
        <div id="wages-step-1" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(1)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_step_1"
                default="Step 1"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[1] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[1] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <!-- Step 1a: Default - uses standard workweek (when user did NOT edit max hours) -->
            <template v-if="!userEditedMaxWorkingHours">
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_step_1a_p1"
                  default="The tool calculates the maximum amount of regular working hours a full-time employee can work in a year in this facility, based on the standard legal work week and your inputs for paid and unpaid leave."
                />
              </p>
              <div class="info-container mt-3">
                <span>
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_step_1_result"
                    default="The full-time working hours per year for this facility are:"
                  />
                </span>
                <div class="value-tag">
                  {{
                    fullTimeWorkingHoursPerYear
                      ? fullTimeWorkingHoursPerYear.toFixed(2)
                      : "-"
                  }}
                  hrs
                </div>
              </div>
              <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
                <p class="text-xs text-gray-300 mb-2">
                  <Translation
                    name="calculation_wizard.preview.sidebar_calculation"
                    default="Calculation"
                  />:
                </p>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_standard_workweek"
                    default="Standard workweek: "
                  />
                  <strong>{{
                    standardWorkweekHours && !isNaN(standardWorkweekHours)
                      ? standardWorkweekHours
                      : "-"
                  }}</strong>
                  hrs
                </p>
                <p class="text-sm text-gray-200">
                  ×
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_weeks_per_year"
                    default="number of weeks in the year:"
                  />
                  <strong>{{
                    weeksPerYear && !isNaN(weeksPerYear) ? weeksPerYear : "-"
                  }}</strong>
                </p>
                <p class="text-sm text-gray-200">
                  -
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_min_paid_leave"
                    default="Min hours of paid leave:"
                  />
                  <strong>{{
                    hoursPaidAnnualLeave ? hoursPaidAnnualLeave.toFixed(2) : "-"
                  }}</strong>
                  hrs
                </p>
                <p class="text-sm text-gray-200">
                  -
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_unpaid_leave"
                    default="Unpaid mandatory leave:"
                  />
                  <strong>{{
                    hoursUnpaidMandatoryLeave
                      ? hoursUnpaidMandatoryLeave.toFixed(2)
                      : "-"
                  }}</strong>
                  hrs
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_fulltime_hours_result"
                    default="Full-time working hours per year:"
                  />
                  <strong>{{
                    fullTimeWorkingHoursPerYear
                      ? fullTimeWorkingHoursPerYear.toFixed(2)
                      : "-"
                  }}</strong>
                  hrs
                </p>
              </div>
            </template>

            <!-- Step 1b: User edited max working hours -->
            <template v-else>
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_step_1b_p1"
                  default="The tool calculates the maximum amount of regular working hours a full-time employee can work in a year in this facility, based on your inputs for maximum hours per year, and paid and unpaid leave."
                />
              </p>
              <div class="info-container mt-3">
                <span>
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_step_1_result"
                    default="The full-time working hours per year for this facility are:"
                  />
                </span>
                <div class="value-tag">
                  {{
                    fullTimeWorkingHoursPerYear
                      ? fullTimeWorkingHoursPerYear.toFixed(0)
                      : "-"
                  }}
                  hrs
                </div>
              </div>
              <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
                <p class="text-xs text-gray-300 mb-2">
                  <Translation
                    name="calculation_wizard.preview.sidebar_calculation"
                    default="Calculation"
                  />:
                </p>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_max_regular_hours"
                    default="Maximum regular working hours per year:"
                  />
                  <strong>{{
                    maxRegularWorkingHoursPerYear
                      ? maxRegularWorkingHoursPerYear.toFixed(0)
                      : "-"
                  }}</strong>
                  hrs
                </p>
                <p class="text-sm text-gray-200">
                  -
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_min_paid_leave"
                    default="Min hours of paid leave:"
                  />
                  <strong>{{
                    hoursPaidAnnualLeave ? hoursPaidAnnualLeave.toFixed(2) : "-"
                  }}</strong>
                  hrs
                </p>
                <p class="text-sm text-gray-200">
                  -
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_unpaid_leave"
                    default="Unpaid mandatory leave:"
                  />
                  <strong>{{
                    hoursUnpaidMandatoryLeave
                      ? hoursUnpaidMandatoryLeave.toFixed(2)
                      : "-"
                  }}</strong>
                  hrs
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_fulltime_hours_result"
                    default="Full-time working hours per year:"
                  />
                  <strong>{{
                    fullTimeWorkingHoursPerYear
                      ? fullTimeWorkingHoursPerYear.toFixed(2)
                      : "-"
                  }}</strong>
                  hrs
                </p>
              </div>
            </template>
          </div>
        </div>

        <!-- Step 2: Share of year calculation -->
        <div id="wages-step-2" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(2)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_step_2"
                default="Step 2"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[2] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[2] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <p>
              <Translation
                name="calculation_wizard.preview.sidebar_wages_step_2a_p1"
                default="The tool calculates the <strong>share of the year</strong> the employee has worked."
              />
            </p>
            <div class="info-container mt-3">
              <span>
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_step_2a_result"
                  default="For this job category or individual, the <strong>'share of year worked'</strong> is:"
                />
              </span>
              <div class="value-tag">
                {{
                  rawShareOfYearWorked
                    ? rawShareOfYearWorked.toFixed(2)
                    : shareOfYearWorked
                }}%
              </div>
            </div>

            <!-- Show correction notice when overtime exceeded -->
            <div
              v-if="overtimeHoursExceeded"
              class="bg-orange-900/30 border border-orange-600 rounded p-3 mt-3"
            >
              <p class="text-orange-300 text-sm">
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_step_2b_correction"
                  default="The tool corrects this to:"
                />
                <strong class="ml-1">100%</strong>
              </p>
            </div>

            <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
              <p class="text-xs text-gray-300 mb-2">
                <Translation
                  name="calculation_wizard.preview.sidebar_calculation"
                  default="Calculation"
                />:
              </p>
              <p class="text-sm text-gray-200">
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_ord_hours_calc"
                  default="number of ordinary hours worked during the year:"
                />
                <strong>{{ hoursWorked }}</strong>
                hrs
              </p>
              <p class="text-sm text-gray-200">
                ÷
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_fulltime_hours"
                  default="Full-time working hours per year:"
                />
                <strong>{{
                  fullTimeWorkingHoursPerYear
                    ? fullTimeWorkingHoursPerYear.toFixed(0)
                    : "-"
                }}</strong>
                hrs
              </p>
              <hr class="my-2 border-gray-600" />
              <p class="text-sm text-gray-200">
                =
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_percentage_year"
                  default="Percentage of the year worked:"
                />
                <strong
                  >{{
                    rawShareOfYearWorked
                      ? rawShareOfYearWorked.toFixed(2)
                      : shareOfYearWorked
                  }}%</strong
                >
              </p>
            </div>

            <!-- Warning for overtime -->
            <p
              v-if="overtimeHoursExceeded"
              class="mt-3 text-orange-300 text-sm"
            >
              <Translation
                name="calculation_wizard.preview.sidebar_wages_step_2b_warning"
                default="Hours above the annual maximum, and wages earned during these hours should be classified as overtime. You may need to correct your inputs."
              />
            </p>
          </div>
        </div>

        <!-- Step 3: Comparable annual full-time wage -->
        <div id="wages-step-3" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(3)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_step_3"
                default="Step 3"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[3] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[3] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <p>
              <Translation
                name="calculation_wizard.preview.sidebar_wages_step_3_p1"
                default="The tool uses <strong>'share of the year'</strong> to calculate what the employee would earn, if working full-time for a year."
              />
            </p>
            <div class="info-container mt-3">
              <span>
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_step_3_result"
                  default="For this job category or individual, the comparable annual full-time wage is:"
                />
              </span>
              <div class="value-tag">
                {{
                  comparableAnnualWage ? comparableAnnualWage.toFixed(2) : "-"
                }}
                {{ currency }}
              </div>
            </div>
            <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
              <p class="text-xs text-gray-300 mb-2">
                <Translation
                  name="calculation_wizard.preview.sidebar_calculation"
                  default="Calculation"
                />:
              </p>
              <!-- Step 3a: No overtime - show wage earned -->
              <template v-if="!overtimeHoursExceeded">
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_wage_earned"
                    default="Wage earned during ordinary hours:"
                  />
                  <strong>{{
                    wageEarned ? wageEarned.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  ÷
                  <Translation
                    name="calculation_wizard.preview.sidebar_share_worked"
                    default="Share of the year worked:"
                  />
                  <strong
                    >{{
                      shareOfYearWorked
                        ? Number(shareOfYearWorked).toFixed(2)
                        : "-"
                    }}%</strong
                  >
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_comparable_annual"
                    default="Comparable annual full-time wage:"
                  />
                  <strong>{{
                    comparableAnnualWage ? comparableAnnualWage.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
              </template>
              <!-- Step 3b: Overtime exceeded - show corrected wage and 100% share -->
              <template v-else>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_corrected_wage"
                    default="Corrected wage earned during ordinary hours:"
                  />
                  <strong>{{
                    comparableAnnualWage ? comparableAnnualWage.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_share_worked"
                    default="Share of the year worked:"
                  />
                  <strong>100%</strong>
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_wages_comparable_annual"
                    default="Comparable annual full-time wage:"
                  />
                  <strong>{{
                    comparableAnnualWage ? comparableAnnualWage.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
              </template>
            </div>
            <!-- Note for Step 3a (no overtime) -->
            <p v-if="!overtimeHoursExceeded" class="mt-3 text-sm text-gray-300">
              <Translation
                name="calculation_wizard.preview.sidebar_wages_step_3a_note"
                default="Note that overtime hours, and wages earned during those hours, are not included in the calculation of the comparable remuneration."
              />
            </p>
            <!-- Note for Step 3b (overtime exceeded) -->
            <p v-else class="mt-3 text-sm text-gray-300">
              <Translation
                name="calculation_wizard.preview.sidebar_wages_step_3b_note"
                default="Note that overtime hours, and wages earned during those hours, are not included in the calculation of the comparable remuneration. The wages you entered are corrected to exclude overtime wages since they were earned during hours above the legal maximum."
              />
            </p>
          </div>
        </div>

        <!-- Step 4: Comparable monthly wage -->
        <div id="wages-step-4" class="accordion">
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="toggleStep(4)"
          >
            <span class="font-bold text-white">
              <Translation
                name="calculation_wizard.preview.sidebar_step_4"
                default="Step 4"
              />
            </span>
            <ChevronDownWhite
              class="transition-transform"
              :class="{ 'rotate-180': stepsOpen[4] }"
            />
          </div>
          <div
            class="info-text accordion-content max-h-0"
            :class="stepsOpen[4] ? 'opacity-1 mt-4' : 'opacity-0'"
          >
            <p>
              <Translation
                name="calculation_wizard.preview.sidebar_wages_step_4_p1"
                default="To be able to compare this to a living wage estimate, the annual value of wages is divided by 12 months."
              />
            </p>
            <div class="info-container mt-3">
              <span>
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_step_4_result"
                  default="For this job category or individual, the comparable monthly wage is:"
                />
              </span>
              <div class="value-tag">
                {{
                  comparableMonthlyWage ? comparableMonthlyWage.toFixed(2) : "-"
                }}
                {{ currency }}
              </div>
            </div>
            <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
              <p class="text-xs text-gray-300 mb-2">
                <Translation
                  name="calculation_wizard.preview.sidebar_calculation"
                  default="Calculation"
                />:
              </p>
              <p class="text-sm text-gray-200">
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_comparable_annual"
                  default="Comparable annual full-time wage:"
                />
                <strong>{{
                  comparableAnnualWage ? comparableAnnualWage.toFixed(2) : "-"
                }}</strong>
                {{ currency }}
              </p>
              <p class="text-sm text-gray-200">
                ÷ 12
                <Translation
                  name="calculation_wizard.preview.sidebar_months"
                  default="months"
                />
              </p>
              <hr class="my-2 border-gray-600" />
              <p class="text-sm text-gray-200">
                =
                <Translation
                  name="calculation_wizard.preview.sidebar_wages_comparable_monthly"
                  default="Comparable monthly full-time wage:"
                />
                <strong>{{
                  comparableMonthlyWage ? comparableMonthlyWage.toFixed(2) : "-"
                }}</strong>
                {{ currency }}
              </p>
            </div>
            <p class="mt-3">
              <Translation
                name="calculation_wizard.preview.sidebar_step_final_note"
                default="This number is displayed in your chart."
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import ChevronDownWhite from "../../icons/ChevronDownWhite.vue";

const props = defineProps({
  currency: {
    type: String,
    required: true,
  },
  hoursWorked: {
    type: Number,
    required: true,
  },
  wageEarned: {
    type: Number,
    required: true,
  },
  shareOfYearWorked: {
    type: Number,
    required: true,
  },
  comparableAnnualWage: {
    type: Number,
    required: true,
  },
  comparableMonthlyWage: {
    type: Number,
    required: true,
  },
  jobCategory: {
    type: String,
    default: "",
  },
  gender: {
    type: String,
    default: "",
  },
  // New props for enhanced calculation explanation
  maxRegularWorkingHoursPerYear: {
    type: Number,
    default: 0,
  },
  fullTimeWorkingHoursPerYear: {
    type: Number,
    default: 0,
  },
  hoursPaidAnnualLeave: {
    type: Number,
    default: 0,
  },
  hoursUnpaidMandatoryLeave: {
    type: Number,
    default: 0,
  },
  rawShareOfYearWorked: {
    type: Number,
    default: 0,
  },
  overtimeHoursExceeded: {
    type: Boolean,
    default: false,
  },
  correctedWageEarned: {
    type: Number,
    default: 0,
  },
  standardWorkweekHours: {
    type: Number,
    default: 0,
  },
  weeksPerYear: {
    type: Number,
    default: 52.14,
  },
});

// Determine if user edited max working hours (for Step 1a vs Step 1b display)
// This is true when maxRegularWorkingHoursPerYear differs from standard calculation
const userEditedMaxWorkingHours = computed(() => {
  if (!props.standardWorkweekHours || !props.weeksPerYear) return false;
  const standardMaxHours = props.standardWorkweekHours * props.weeksPerYear;
  // If the max regular hours differs significantly from standard calculation, user edited it
  return (
    props.maxRegularWorkingHoursPerYear &&
    Math.abs(props.maxRegularWorkingHoursPerYear - standardMaxHours) > 1
  );
});

const stepsOpen = ref({
  1: true,
  2: false,
  3: false,
  4: false,
});

const toggleStep = (step) => {
  const accordion = document.getElementById(`wages-step-${step}`);
  if (!accordion) return;
  const accordionContent = accordion.querySelector(".accordion-content");
  if (!accordionContent) return;
  if (stepsOpen.value[step]) {
    accordionContent.style.maxHeight = 0;
  } else {
    accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
  }
  stepsOpen.value[step] = !stepsOpen.value[step];
};

onMounted(() => {
  const accordion = document.getElementById("wages-step-1");
  if (accordion) {
    const accordionContent = accordion.querySelector(".accordion-content");
    if (accordionContent) {
      accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
    }
  }
});
</script>
